// MSAL.js singleton + helpers for Microsoft Entra ID sign-in.
//
// Env vars (Vite injects at build time, must be VITE_ prefixed):
//   VITE_ENTRA_TENANT_ID — Azure AD tenant UUID
//   VITE_ENTRA_CLIENT_ID — App registration UUID
//
// If either is missing we DON'T construct an MSAL instance — the LoginPage
// hides the Microsoft button and falls back to dev presets. That keeps local
// development working without forcing every dev to register an app.
//
// Token used: ID token (claims signed by Entra). For Phase 1 our backend
// validates ID tokens directly (audience = client_id). Access tokens with
// custom scope-based access control can swap in later without UI changes.

import {
  type AccountInfo,
  type Configuration,
  EventType,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;

export const ENTRA_ENABLED = Boolean(TENANT_ID && CLIENT_ID);

/** Scopes requested at login. openid + profile + email give us oid, name, upn. */
export const LOGIN_REQUEST: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/** Singleton MSAL instance. Null when env vars missing (e.g. local dev). */
export const msalInstance: PublicClientApplication | null = ENTRA_ENABLED
  ? new PublicClientApplication(buildConfig())
  : null;

function buildConfig(): Configuration {
  return {
    auth: {
      clientId: CLIENT_ID!,
      authority: `https://login.microsoftonline.com/${TENANT_ID!}`,
      redirectUri: `${window.location.origin}/login/callback`,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    },
    cache: {
      // localStorage > sessionStorage: token survives tab close + 30d remember.
      // MSAL warns about XSS risk; we accept it for SPA usability. CSP + helmet
      // already block inline scripts on internal.tristone-cap.com.
      cacheLocation: 'localStorage',
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message) => {
          if (level <= 1) console.warn(`[MSAL] ${message}`);
        },
        piiLoggingEnabled: false,
      },
    },
  };
}

/** Initialize MSAL once at app startup. Idempotent. */
let initialized: Promise<void> | null = null;
export async function initMsal(): Promise<void> {
  if (!msalInstance) return;
  if (initialized) return initialized;
  initialized = (async () => {
    await msalInstance.initialize();
    // Process redirect response if returning from Entra sign-in.
    await msalInstance.handleRedirectPromise();
    // Set the first signed-in account as active (MSAL doesn't auto-pick).
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }
    // Keep active account in sync on login events (multi-account scenarios).
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const account = (event.payload as { account?: AccountInfo }).account;
        if (account) msalInstance.setActiveAccount(account);
      }
    });
  })();
  return initialized;
}

/** Acquire an ID token silently (uses cached refresh token); returns null if
 *  not signed in or refresh failed. Caller should redirect to login when null. */
export async function getIdToken(): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...LOGIN_REQUEST,
      account,
    });
    return result.idToken;
  } catch (err) {
    console.warn('[MSAL] silent token acquisition failed:', err);
    return null;
  }
}

/** Start Entra sign-in via full-page redirect. */
export async function loginRedirect(): Promise<void> {
  if (!msalInstance) {
    throw new Error('MSAL not configured — set VITE_ENTRA_TENANT_ID + VITE_ENTRA_CLIENT_ID');
  }
  await msalInstance.loginRedirect(LOGIN_REQUEST);
}

/** Sign out + clear MSAL cache + redirect to /login. */
export async function logoutRedirect(): Promise<void> {
  if (!msalInstance) return;
  const account = msalInstance.getActiveAccount();
  await msalInstance.logoutRedirect({
    account: account ?? undefined,
    postLogoutRedirectUri: `${window.location.origin}/login`,
  });
}

/** Current signed-in user info (after initMsal completes), or null. */
export function getActiveAccount(): AccountInfo | null {
  return msalInstance?.getActiveAccount() ?? null;
}
